import { filter } from "lodash"
import React, { Dispatch, SetStateAction, useEffect, useState } from "react"

import { iArticleTranslation } from "../../types"
import ToggleMachine from "../Utils/ToggleMachine"

const DropdownToggleButtonView: React.FC<{
  onClick?: () => void
  className?: string
  title: string
}> = ({ className, title, onClick }) => (
  <button
    onClick={onClick}
    className={["px-2 text-white text-xs rounded-t", className].join(" ")}
    style={{ zIndex: 1, marginTop: "-30px", minHeight: "30px" }}
  >
    {title}
  </button>
)

const DropdownToggleButton: React.FC<{
  open: () => void
  close: () => void
  isOpen: () => boolean
}> = ({ open, close, isOpen }) => {
  if (isOpen()) {
    return (
      <DropdownToggleButtonView
        title={"Close translations"}
        onClick={close}
        className={"bg-red-600"}
      />
    )
  }
  return (
    <DropdownToggleButtonView
      title={"Translations"}
      onClick={open}
      className={"bg-site-blue-dark button-bg-site-blue-dark"}
    />
  )
}

const DropDownSelectButton: React.FC<{
  title: string
  onClick?: () => void
  selected: boolean
}> = ({ title, onClick, selected }) => (
  <button
    onClick={onClick}
    className={[
      "w-full block p-1",
      selected
        ? "bg-site-green text-white"
        : "hover:bg-gray-200 hover:text-blue-900 ",
    ].join(" ")}
  >
    {title}
  </button>
)

const LanguageDropdownList: React.FC<{
  close: () => void
  isClose: () => boolean
  translationsLanguageList: string[]
  selectedLanguage?: string
  updateSelectedLanguage: Dispatch<SetStateAction<string | undefined>>
}> = ({
  close,
  isClose,
  translationsLanguageList,
  selectedLanguage,
  updateSelectedLanguage,
}) => {
  if (isClose()) return null
  return (
    <div
      className="min-w-full bg-white shadow text-sm rounded-b  overflow-hidden"
      style={{ zIndex: 1 }}
    >
      <DropDownSelectButton
        title="English"
        onClick={() => {
          updateSelectedLanguage(undefined)
          close()
        }}
        selected={selectedLanguage === undefined}
      />
      {translationsLanguageList.map((language, index) => (
        <DropDownSelectButton
          key={index}
          title={language}
          onClick={() => {
            updateSelectedLanguage(language)
            close()
          }}
          selected={selectedLanguage === language}
        />
      ))}
    </div>
  )
}

const TranslationsDropdown: React.FC<{
  translationsLanguageList: string[]
  updateSelectedLanguage: Dispatch<SetStateAction<undefined | string>>
  selectedLanguage?: string
}> = ({
  translationsLanguageList,
  updateSelectedLanguage,
  selectedLanguage,
}) => {
  return (
    <div className="absolute flex-col items-end top-0 right-0 translation-dropdown-container">
      <ToggleMachine>
        {(toggle, open, isOpen, close, isClose) => {
          return (
            <>
              <DropdownToggleButton open={open} close={close} isOpen={isOpen} />
              <LanguageDropdownList
                selectedLanguage={selectedLanguage}
                updateSelectedLanguage={updateSelectedLanguage}
                translationsLanguageList={translationsLanguageList}
                close={close}
                isClose={isClose}
              />
            </>
          )
        }}
      </ToggleMachine>
    </div>
  )
}

const withTranslations = (
  defaultText: string,
  translations: iArticleTranslation[] | undefined,
  defaultTranslation: string | undefined,
  Component: React.FC<{ text: string }>
) => {
  if (!translations) {
    return () => <Component text={defaultText} />
  }
  translations = translations.filter(item => Boolean(item))
  if (translations.length === 0) {
    return () => <Component text={defaultText} />
  }
  return () => {
    // This component assumes English to be default language. As that when English is selected
    // the state "selectedLanguage" becomes "undefined".
    const _translations = translations
    const translationsLanguageList = _translations!.map(item => {
      return item.language
    })
    const [selectedLanguage, updateSelectedLanguage] = useState<
      string | undefined
    >(defaultTranslation)
    const [displayText, updateDisplayText] = useState<string>(defaultText)
    const rightHandedLanguages = ["Urdu", "Arabic"]
    const [textRight, setTextRight] = useState(false)

    useEffect(() => {
      if (!selectedLanguage) {
        updateDisplayText(defaultText)
        setTextRight(false)
      } else {
        const selectedTranslation = filter(_translations, {
          language: selectedLanguage,
        })[0]
        updateDisplayText(selectedTranslation.content)
        setTextRight(
          rightHandedLanguages.filter(language => language === selectedLanguage)
            .length > 0
        )
      }
    }, [selectedLanguage])

    return (
      <div className="relative hover:bg-gray-200 rounded-l rounded-br translatable-text-container">
        <TranslationsDropdown
          translationsLanguageList={translationsLanguageList}
          updateSelectedLanguage={updateSelectedLanguage}
          selectedLanguage={selectedLanguage}
        />
        <div className={textRight ? "text-right" : "text-left"}>
          <Component text={displayText} />
        </div>
      </div>
    )
  }
}

export default withTranslations
